import React from "react";

import { PartitionLine } from "./PartitionLine";

const AUTOSCROLL_SPEED = 0.06;
const AUTOSCROLL_POSITION = 200;
const WAVE_CONTAINER_WIDTH = 800;

/**
 * AudioTimePartitionLine displays and controls a line marking the current playback position
 * of the audio.
 *
 * Like AudioWave, AudioTimePartitionLine parameters work in the frame of 0-1.
 * All the locations are placed within that range and then scaled by svgLength.
 * All actual timestamps are in that range and then scaled by audioDuration.
 *
 * @param props is described below
 * @param props.audioRef is a reference to the audio that the AudioWave visualizes. This is used to control the audio cursor.
 * @param props.containerRef is a reference to the container of the svg element. This is used to automatically scroll while the audio plays.
 * @param props.svgLength is the total length of the svg container, use to scale positions.
 * @param props.svgHeight is the height of the svg container in pixels
 * @param props.svgPaddingX is the amount of padding that should be accounted for, used to calculate positions.
 */
export const AudioTimePartitionLine = ({
  audioRef,
  containerRef,
  svgLength,
  svgHeight,
  svgPaddingX,
}: {
  audioRef: React.MutableRefObject<HTMLAudioElement | undefined>;
  containerRef: React.RefObject<HTMLDivElement>;
  svgLength: number;
  svgHeight: number;
  svgPaddingX: number;
}) => {
  const HIDDEN_PLAYBACK_TIME_PLACEMENT = -100;

  const [playbackTimePlacement, setPlaybackTimePlacement] = React.useState(
    HIDDEN_PLAYBACK_TIME_PLACEMENT,
  );
  const [lockScroll, setLockScroll] = React.useState(false);
  const [isBeingDragged, setIsBeingDragged] = React.useState(false);
  const requestRef = React.useRef<number>();

  const updatePlaybackTimeLoop = React.useCallback(() => {
    // When duration is NaN, have playback show at the beginning
    const duration = audioRef.current?.duration ?? 1;
    const newPlaybackTime = isNaN(duration)
      ? 0
      : (audioRef.current?.currentTime ?? 0) / duration;
    const newPlaybackPosition = newPlaybackTime * svgLength;

    if (!isBeingDragged) {
      if (
        containerRef.current &&
        newPlaybackPosition === 0 &&
        // If we've already reset the scroll, don't do it again
        newPlaybackPosition !== playbackTimePlacement
      ) {
        // Reset scroll width on end of playback
        containerRef.current.scrollLeft = 0;
      }
      setPlaybackTimePlacement(newPlaybackPosition);
    }

    if (containerRef.current && audioRef.current && !audioRef.current.paused) {
      if (
        !lockScroll &&
        (containerRef.current.scrollLeft <
          newPlaybackPosition - WAVE_CONTAINER_WIDTH ||
          containerRef.current.scrollLeft > newPlaybackPosition)
      ) {
        setLockScroll(true);
      }

      if (lockScroll) {
        const scrollTarget = newPlaybackPosition - AUTOSCROLL_POSITION;
        const currentScroll = containerRef.current.scrollLeft;
        const deltaScroll = scrollTarget - currentScroll;
        const amountToScroll = deltaScroll * AUTOSCROLL_SPEED;
        containerRef.current.scrollLeft = currentScroll + amountToScroll;
      }
    }

    if (audioRef.current?.paused && lockScroll) {
      setLockScroll(false);
    }

    requestRef.current = requestAnimationFrame(updatePlaybackTimeLoop);
  }, [
    audioRef,
    containerRef,
    isBeingDragged,
    lockScroll,
    svgLength,
    playbackTimePlacement,
  ]);

  React.useEffect(() => {
    requestRef.current = requestAnimationFrame(updatePlaybackTimeLoop);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [updatePlaybackTimeLoop]);

  return (
    <PartitionLine
      initialXPosition={playbackTimePlacement}
      label=""
      onPartitionLineMouseDown={() => setIsBeingDragged(true)}
      onPartitionLineMouseUp={(xPosition: number) => {
        setIsBeingDragged(false);

        if (audioRef.current) {
          audioRef.current.currentTime =
            (xPosition / svgLength) * audioRef.current.duration;
        }
      }}
      partitionType="time"
      svgHeight={svgHeight}
      svgLength={svgLength}
      svgPaddingX={svgPaddingX}
    />
  );
};
